import { LegalLayout, H2, P, UL, Mail, COMPANY, PRODUCT } from "@/components/LegalLayout";

export default function PrivacyPolicy() {
    return (
        <LegalLayout eyebrow="Legal" title="Privacy Policy" lastUpdated="June 2026" testid="privacy-policy-page">
            <P>
                This Privacy Policy explains how {COMPANY} (“we”, “us”, “our”) collects, uses, stores, and
                protects information when you or your organization use {PRODUCT} (the “Service”) — our
                cloud-based call center, softphone/CTI, and WhatsApp business messaging product. It also
                explains the rights available to you and how to contact us.
            </P>
            <P>
                {PRODUCT} is a business-to-business platform. Our direct customers are the businesses
                (“Workspaces”) that use the Service to communicate with their own customers and contacts
                (“End Users”). For End-User personal data processed through the Service, the Workspace is the
                data controller and {COMPANY} acts as a data processor on the Workspace’s behalf.
            </P>

            <H2>Information We Collect</H2>
            <UL items={[
                "Account & workspace data: names, work email addresses, phone numbers, roles, and organization details of the agents, supervisors, and admins who use the Service.",
                "Telephony data: call metadata (numbers dialed/received, timestamps, duration, direction, disposition), and — where the Workspace enables it — call recordings and AI-generated transcripts and summaries.",
                "WhatsApp / messaging data: messages exchanged through the WhatsApp Business Cloud API (text, media, interactive menu selections), the End User’s WhatsApp phone number and profile name, delivery/read status, and bot conversation state.",
                "Lead & CRM data: information captured during a conversation (such as name, email, phone, company, and enquiry details) and, where connected, records synced to a Workspace’s CRM (e.g. Salesforce).",
                "Technical data: log data, IP address, cookies and similar technologies, device and browser identifiers, and diagnostic events used to operate and secure the Service.",
            ]} />

            <H2>How We Use Information</H2>
            <UL items={[
                "To provide and operate the Service — routing calls and messages, running WhatsApp bot flows, capturing leads/cases, and displaying conversations to authorized Workspace users.",
                "To generate optional AI features a Workspace turns on, such as call summaries and sentiment analysis.",
                "To secure the Service, prevent abuse, troubleshoot, and maintain reliability.",
                "To comply with legal obligations and applicable messaging platform policies (including Meta/WhatsApp’s policies).",
            ]} />
            <P>
                We do not sell personal data. We do not use End-User WhatsApp content for advertising, and we
                only send WhatsApp messages consistent with the WhatsApp Business Messaging Policy and the
                relevant customer-service window / opt-in rules.
            </P>

            <H2>Third Parties & Sub-processors</H2>
            <P>To deliver the Service we rely on trusted providers that process data on our behalf, including:</P>
            <UL items={[
                "Communications providers configured by the Workspace (e.g. Twilio, Plivo, Exotel) to place and receive calls.",
                "Meta Platforms, Inc. — for delivery of WhatsApp Business messages via the WhatsApp Cloud API.",
                "AI/LLM and transcription providers configured by the Workspace, used only to power features the Workspace enables.",
                "Cloud hosting, database, and object-storage infrastructure used to run and store the Service’s data.",
                "Payment processors (e.g. Razorpay) for billing, where applicable.",
            ]} />
            <P>
                Many integrations use a “bring-your-own-key” model, meaning the Workspace supplies its own
                provider credentials and its data flows to those providers under the Workspace’s own accounts
                and agreements.
            </P>

            <H2>Data Storage & Security</H2>
            <UL items={[
                "Data is stored in access-controlled cloud infrastructure, logically isolated per Workspace (each record is stamped with its organization identifier).",
                "Sensitive credentials (such as provider API keys) are encrypted at rest.",
                "Access is restricted to authorized personnel and to the Workspace users entitled to it, and is transmitted over encrypted (HTTPS/TLS) connections.",
            ]} />

            <div id="data-deletion" className="scroll-mt-24 space-y-4">
                <H2>Data Retention & Deletion</H2>
                <P>
                    We retain personal data for as long as needed to provide the Service to the relevant
                    Workspace or as required by law. You can request access to, or deletion of, your personal
                    data at any time — <strong>free of charge</strong> — by emailing <Mail /> with the subject
                    line “Data Deletion Request”. A Workspace may also request export or deletion of its data.
                </P>
                <P>
                    We action verified requests within a reasonable period (typically within 30 days), subject
                    to legal retention requirements, and we do not charge any fee for such requests. Step-by-step
                    instructions are available on our{" "}
                    <a href="/data-deletion" className="font-semibold text-brand hover:underline">Data Deletion</a>{" "}
                    page.
                </P>
                <P>
                    For WhatsApp messages, Meta Platforms, Inc. acts as a processor and retains message content on
                    its systems for up to 30 days, as described in the WhatsApp Cloud API documentation. Deleting
                    your data with us does not by itself remove data held independently by other controllers or
                    platforms.
                </P>
            </div>

            <H2>Your Rights</H2>
            <P>
                Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict the
                processing of your personal data, and to withdraw consent. To exercise these rights, contact us
                at <Mail />. If you are an End User, you may also contact the Workspace you interacted with, as
                they control your data.
            </P>

            <H2>Children</H2>
            <P>The Service is intended for business use and is not directed to individuals under 18.</P>

            <H2>Changes to This Policy</H2>
            <P>
                We may update this Policy from time to time. Material changes will be reflected by updating the
                “Last updated” date above.
            </P>

            <H2>Contact Us</H2>
            <P>
                For any privacy questions or requests, contact {COMPANY} at <Mail />.
            </P>
        </LegalLayout>
    );
}
